import React from 'react';
import { attendanceAPI } from '../services/api';
import ScopeBadge from '../components/ScopeBadge';
import StatusChip from '../components/StatusChip';
import MonthField from '../components/MonthField';
import useScopeFilters from '../hooks/useScopeFilters';
import { formatDateLabel, todayString } from '../utils/format';
import { downloadBlob } from '../utils/download';
import {
    Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Skeleton, Alert, Stack, Button, Select, MenuItem, FormControl, InputLabel,
    Pagination, Dialog, DialogTitle, DialogContent, IconButton, TextField, InputAdornment,
} from '@mui/material';
import { FileDownloadRounded, CloseRounded, SearchRounded } from '@mui/icons-material';

const ALL = '';
const CURRENT_MONTH = todayString().slice(0, 7);

function monthLabel(month) {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function DetailDialog({ userId, month, onClose }) {
    const [data, setData]       = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError]     = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        attendanceAPI.detail(userId, { month })
            .then((res) => {
                if (cancelled) return;
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load attendance detail');
                setData(res.data);
            })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load attendance detail'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [userId, month]);

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h3">{data?.employee?.name || 'Attendance Detail'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {data?.employee?.employee_id} · {monthLabel(month)}
                        {data?.shift && ` · ${data.shift.shift_title || ''} ${data.shift.start_time}–${data.shift.end_time}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseRounded /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, maxHeight: 480 }}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ p: 2 }}>
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1, mb: 1 }} />
                        ))}
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Check-in</TableCell>
                                <TableCell>Check-out</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.days || []).map((d) => (
                                <TableRow key={d.date} sx={{ '&:last-child td': { border: 0 } }}>
                                    <TableCell>{formatDateLabel(d.date)}</TableCell>
                                    <TableCell>{d.checkin_time || '—'}</TableCell>
                                    <TableCell>{d.checkout_time || '—'}</TableCell>
                                    <TableCell>{d.status ? <StatusChip status={d.status} /> : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function Attendance() {
    const [month, setMonth]       = React.useState(CURRENT_MONTH);
    const [search, setSearch]     = React.useState('');
    const {
        ready: scopeReady, units, teams,
        unitId, setUnitId, teamId, setTeamId, showUnitFilter, showTeamFilter,
    } = useScopeFilters('reports.attendance');
    const [page, setPage]         = React.useState(1);
    const [records, setRecords]   = React.useState([]);
    const [pagination, setPagination] = React.useState({ total: 0, pages: 1 });
    const [loading, setLoading]   = React.useState(true);
    const [exporting, setExporting] = React.useState(false);
    const [error, setError]       = React.useState('');
    const [detailUser, setDetailUser] = React.useState(null);

    React.useEffect(() => { setPage(1); }, [month, unitId, teamId, search]);

    React.useEffect(() => {
        if (!scopeReady) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        const handle = setTimeout(() => {
            attendanceAPI.list({ month, unit_id: unitId || undefined, team_id: teamId || undefined, search: search || undefined, page })
                .then((res) => {
                    if (cancelled) return;
                    if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load attendance');
                    setRecords(res.data.records || []);
                    setPagination(res.data.pagination || { total: 0, pages: 1 });
                })
                .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load attendance'); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [scopeReady, month, unitId, teamId, search, page]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await attendanceAPI.exportCsv({ month, unit_id: unitId || undefined, team_id: teamId || undefined, search: search || undefined });
            downloadBlob(res.data, `attendance-${month}.csv`);
        } catch (err) {
            setError('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h2" sx={{ mb: 0.5 }}>Attendance</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">
                            {loading ? 'Loading…' : `${pagination.total} ${pagination.total === 1 ? 'employee' : 'employees'} · ${monthLabel(month)}`}
                        </Typography>
                        <ScopeBadge featureKey="reports.attendance" />
                    </Stack>
                </Box>
                <Button
                    variant="outlined" size="small" startIcon={<FileDownloadRounded />}
                    onClick={handleExport} disabled={exporting || loading}
                >
                    {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
            </Stack>

            {/* Filters */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                <TextField
                    size="small" placeholder="Search name or employee ID…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <MonthField value={month} onChange={setMonth} />
                {showUnitFilter && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                            <MenuItem value={ALL}>All Units</MenuItem>
                            {units.map((u) => <MenuItem key={u.unit_id} value={u.unit_id}>{u.unit_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
                {showTeamFilter && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Team</InputLabel>
                        <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                            <MenuItem value={ALL}>All Teams</MenuItem>
                            {teams.map((t) => <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell align="right">Total Working Days</TableCell>
                                <TableCell align="right">Present</TableCell>
                                <TableCell align="right">Absent</TableCell>
                                <TableCell align="right">Lates</TableCell>
                                <TableCell align="right">Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={6}>
                                            <Skeleton variant="rectangular" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No employees found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                records.map((r) => (
                                    <TableRow key={r.user_id} sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{r.employee_id}</Typography>
                                        </TableCell>
                                        <TableCell align="right">{r.total_working_days}</TableCell>
                                        <TableCell align="right" sx={{ color: '#10B981' }}>{r.present}</TableCell>
                                        <TableCell align="right" sx={{ color: '#EF4444' }}>{r.absent}</TableCell>
                                        <TableCell align="right" sx={{ color: '#F59E0B' }}>{r.late}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => setDetailUser(r.user_id)}>Details</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <Pagination count={pagination.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
                    </Box>
                )}
            </Paper>

            {detailUser && (
                <DetailDialog userId={detailUser} month={month} onClose={() => setDetailUser(null)} />
            )}
        </Box>
    );
}
